import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {AutoCompleteSearchField, AutoCompleteSearchOption} from "@bizform/type/model/data_set/Lookup.model";
import {LookUp} from "@bizform/type/model/data_type/LookUp.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {LookUpSearchFieldKeyConstant} from "@bizform/type/model/component_element/manifest_constant/LookUp.constant";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {
    AccessibilityDropdown,
    AutoComplete,
    AutoCompleteProps,
    ComponentHelper,
    Debug,
    DomHelper,
    ObjectHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


let ___autoComplete;

type Props = AutoCompleteProps & {
};

type WrappedCompState = {
    id?: string;
    value?: any;
    name?: string;
    suggestions?: any[];
    field?: string;
    scrollHeight?: string;
    dropdown?: boolean;
    dropdownMode?: string;
    multiple?: boolean;
    minLength?: number;
    delay?: number;
    style?: object;
    className?: string;
    inputId?: string;
    inputStyle?: object;
    inputClassName?: string;
    printStyle?: object;
    printClassName?: string;
    placeholder?: string;
    readonly?: boolean;
    disabled?: boolean;
    maxlength?: number;
    size?: number;
    appendTo?: any;
    tabindex?: number;
    tooltipAttribute?: TooltipOptions & {text?: string};
    maxCount?: number;
    searchOption?: string;
    [LookUpSearchFieldKeyConstant]?: AutoCompleteSearchField;
    items?: Array<LookUp>;

    completeMethod?(e: {originalEvent: Event, query: string}): void;
    itemTemplate?(data: any): JSX.Element | undefined;
    selectedItemTemplate?(data: any): JSX.Element | undefined;
    onChange?(e: ComponentEventType): void;
    onFocus?(event: Event): void;
    onBlur?(event: Event): void;
    onSelect?(e: ComponentEventType): void;
    onUnselect?(e: ComponentEventType): void;
    onDropdownClick?(e: {originalEvent: Event, query: string}): void;
    onClick?(event: Event): void;
    onDblClick?(event: Event): void;
    onMouseDown?(event: Event): void;
    onKeyUp?(event: Event): void;
    onKeyPress?(event: Event): void;
    onContextMenu?(event: Event): void;
    onClear?(event: Event): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfAutoCompleteMultiple
 * @description BfAutoCompleteMultiple component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<LookUp>>>()
export class BfAutoCompleteMultiple extends AbstractComponentElement<Props, State> {
    static readonly enableLazyLookup: boolean = true;

    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_NmmuOn3UxdDSgshXSLnxFFG7xT_DLPL",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.autocompleteMultiple.label,
        description: I18n.componentElement.autocompleteMultiple.label,
        icon: "",
        dataType: [DataType.LOOKUP],
        onlyArray: true,
        pathToLimitDataTypeArray: "maxCount",
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._autoComplete.validationFocus();
        } else if (this._autoComplete && this._autoComplete.inputEl) {
            this._autoComplete.inputEl.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            newState.wrappedCompState.field = "label";
            newState.wrappedCompState.maxCount = !newState.wrappedCompState.maxCount ? 1 : newState.wrappedCompState.maxCount;

            let isAcceptable = true;
            if (newState.wrappedCompState.value) {
                if (newState.wrappedCompState.value.length > newState.wrappedCompState.maxCount) {
                    newState.wrappedCompState.value = newState.wrappedCompState.value.slice(0, newState.wrappedCompState.maxCount);
                    isAcceptable = false;
                }
            }
            if (isAcceptable) {
                this.setState(newState);
            } else {
                this.setState(newState, this._callbackSetProperty);
            }
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<LookUp> | any): void {
    }

    emitFormState(value: string): void {
    }

    subscription(subscriptionValue: Array<LookUp>): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: null
            }
        };
        let isAcceptable = true;
        if (!ComponentHelper.isNull(subscriptionValue)) {
            if (Array.isArray(subscriptionValue)) {
                if (subscriptionValue.length > 0) {
                    if (this._isLazyLoading) {
                        newState.wrappedCompState.value = subscriptionValue;
                    } else {
                        newState.wrappedCompState.value = subscriptionValue.map((valLookUp) => {
                            const returnValue = newState.wrappedCompState.items && newState.wrappedCompState.items.find ? newState.wrappedCompState.items.find((item) => {
                                return valLookUp.value && valLookUp.value === item.value;
                            }) : null;
                            if (returnValue) {
                                return returnValue;
                            } else {
                                isAcceptable = false;
                                return null;
                            }
                        });
                    }
                }
            } else {
                isAcceptable = false;
            }
        }
        this._orgValue = ObjectHelper.jsonCloneDeep(newState.wrappedCompState.value);

        if (isAcceptable) {
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    lookupDataSubscription(lookup: Array<LookUp>, isLazyLoading?: boolean): void {
        this._isLazyLoading = isLazyLoading;
        if (Array.isArray(lookup)) {
            if (isLazyLoading) {
                // this._autoComplete.searching = true;
                this._query = this._lazyQuery.toLowerCase();
                const newState = {
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        items: null,
                        suggestions: lookup.filter(this._doFilterItem)
                    }
                };
                this.setState(newState, function() {
                    if (!this.state.wrappedCompState.suggestions || this.state.wrappedCompState.suggestions.length < 1) {
                        this.closeOverlay();
                    }
                });
            } else {
                const newState = {
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        items: lookup
                    }
                };
                this.setState(newState);
            }
        }
    }

    emitSubscriptionReject(): void {
    }

    @ReactComponentElementInit(BfAutoCompleteMultiple)
    static init(): ComponentElement<Array<LookUp>> {
        return;
    }

    subscriptionResize(): void {
        if (___autoComplete) {
            for (const panelName in ___autoComplete) {
                const panel = ___autoComplete[panelName];
                if (panel._timer) {
                    clearTimeout(panel._timer);
                }
                panel._timer = setTimeout(panel.alignPanel, 100);
            }
        }
    }

    initDone(): void {
        ___autoComplete[this.elementId] = this;
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _autoComplete: any;
    private _tooltip: Array<any> = [];
    private _isLazyLoading: boolean;
    private _lazyQuery: string;
    private _orgValue: any;
    private _query: string;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };
        this._isLazyLoading = true;
        this._onChangeEvent = this._onChangeEvent.bind(this);
        // this._focusHandler = this._focusHandler.bind(this);
        this._filterItem = this._filterItem.bind(this);
        this._doFilterItem = this._doFilterItem.bind(this);
        this._setRef = this._setRef.bind(this);
        this.subscriptionResize = this.subscriptionResize.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.alignPanel = this.alignPanel.bind(this);
        if (!___autoComplete) {
            ___autoComplete = {};
        }
    }

    componentDidUpdate(): void {
        if (this._autoComplete && this._autoComplete.inputEl && !this._tooltip.length) {
            ComponentHelper.tooltip(this._autoComplete.inputEl, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
        if (this._autoComplete) {
            this._makeStyle();
        }
        if (this.state.wrappedCompState.value && this.state.wrappedCompState.value.length >= this.state.wrappedCompState.maxCount && this._autoComplete && this._autoComplete.container && this._autoComplete.container.querySelector("input")) {
            let spanTag = this._autoComplete.container.querySelector("input");
            spanTag.style.width = "0px";
        }
    }

    componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
        this._removeCustom();
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._autoComplete = null;
        delete ___autoComplete[this.elementId];
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    closeOverlay() {
        if (this._autoComplete && this._autoComplete.hidePanel) {
            this._autoComplete.hidePanel();
        }
    }

    alignPanel() {
        if (this._autoComplete && this._autoComplete.alignPanel) {
            this._autoComplete.alignPanel();
        }
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newProps = this.state.wrappedCompState;
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            this._orgValue = e.value;
            this.setState({wrappedCompState: {
                ...newProps,
                value: e.value
            }}, this._callbackSetState);
        }
    }

    private _callbackSetProperty(): void {
        this.emit(this.state.wrappedCompState.value);
    }

    private _callbackSetState(): void {
        try {
            this.emit(this.state.wrappedCompState.value);
            if (this.state.wrappedFormState && this.state.wrappedFormState.onChange) {
                this.emitFormState(this.state.wrappedFormState.onChange);
            }
        } catch (e) {
            Debug.console.error("_callbackSetState", e);
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        if (newProps.value && newProps.value.length) {
            newProps.placeholder = newProps.value.length >= newProps.maxCount ? "" : newProps.placeholder;
            newProps.suggestions =  newProps.value.length >= newProps.maxCount ? [] : newProps.suggestions;
        }
        delete(newProps.printClassName);
        delete(newProps.printStyle);
        if (this.accessibility) {
            return (
                <AccessibilityDropdown
                    tabindex={newProps.tabindex}
                    key={this.elementId}
                    id={this.elementId}
                    value={newProps.value}
                    orgValue={this._orgValue}
                    options={this._isLazyLoading ? newProps.suggestions : newProps.items}
                    placeholder={newProps.placeholder}
                    maxCount={newProps.maxCount}
                    showClear={true}
                    isMultiple={true}
                    isAutoComplete={true}
                    searchOption={newProps.searchOption}
                    displayMode={this.displayMode}
                    tooltipAttribute={newProps.tooltipAttribute}
                    onChange={newProps.onChange}
                    completeMethod={this._filterItem}
                    ref={this._setRef}
                />
            );
        } else {
            return (
                <AutoComplete
                    {...newProps}
                    id={this.elementId}
                    key={this.elementId}
                    multiple={true}
                    completeMethod={this._filterItem}
                    style={null}
                    appendTo={DomHelper.getForOverLayDom()}
                    ref={this._setRef}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readonly: this.state.wrappedCompState.value && (this.state.wrappedCompState.maxCount <= this.state.wrappedCompState.value.length)
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readonly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };
        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let value: any = "";
        if (this.state.wrappedCompState.value) {
            if (Array.isArray(this.state.wrappedCompState.value)) {
                value = this.state.wrappedCompState.value.map((x, i) => {
                    return x[this.state.wrappedCompState.field];
                });
            } else {
                value = this.state.wrappedCompState.value[this.state.wrappedCompState.field];
            }
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            if (this._tooltip.length) {
                this._removeCustom();
            }
            this._autoComplete = ref;
            this.readyComponent();
            this._makeStyle();
        }
    }

    private _filterItem(e): void {
        // if (this.state.wrappedCompState.value.length >= this.state.wrappedCompState.maxCount) return null;
        if (this._isLazyLoading) {
            this._lazyQuery = e.query;
            if (Array.isArray(this.state.wrappedCompState.value) && this.state.wrappedCompState.value.length) {
                this.emit(this.state.wrappedCompState.value.concat({ label: e.query }));
            } else {
                this.emit({ label: e.query });
            }
        } else if (this.state.wrappedCompState.items && this.state.wrappedCompState.items.length > 0) {
            this._query = e.query.toLowerCase();
            this.setState({
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        suggestions: this.state.wrappedCompState.items.filter(this._doFilterItem)
                    }
                }, function() {
                    if (this.state.wrappedCompState.suggestions.length < 1) {
                        this.closeOverlay();
                    }
                    this._callbackSetState();
                }
            );
        }
    }

    private _doFilterItem(item: LookUp): boolean {
        if (AutoCompleteSearchOption.NONE === this.state.wrappedCompState.searchOption) {
            return true;
        }
        const f = this.state.wrappedCompState[LookUpSearchFieldKeyConstant];
        const l = f === AutoCompleteSearchField.LABELVALUE ? ["label", "value"] : (f === AutoCompleteSearchField.VALUE ? ["value"] : ["label"]);
        let result = false;
        for (let i = 0; i < l.length; i++) {
            if (AutoCompleteSearchOption.INCLUDES === this.state.wrappedCompState.searchOption) {
                if (String(item[l[i]]).toLowerCase().includes(this._query)) {
                    result = true;
                    break;
                }
            } else {
                if (String(item[l[i]]).toLowerCase().startsWith(this._query)) {
                    result = true;
                    break;
                }
            }
        }
        return result;
    }

    private _removeCustom(): void {
        ComponentHelper.unbindTooltip(this._tooltip);
    }

    private _makeStyle(newProps = {...this.state.wrappedCompState}) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._autoComplete && this._autoComplete.container) {
                let spanTag = this._autoComplete.container.querySelector("ul");
                spanTag.setAttribute("style", "");
                for (let item in newProps.style) {
                    spanTag.style[item] = newProps.style[item];
                }
            }
        }
    }


}
